import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { uniqueId } from "lodash";
import { useUnmount } from "react-use";

type Counters = { [key: string]: number };

const AttachmentsContext = React.createContext<{
  counters: Counters;
  setCounters: Dispatch<SetStateAction<Counters>>;
}>({
  counters: {},
  setCounters: () => {},
});

export const AttachmentsProvider: FC = ({ children }) => {
  const [counters, setCounters] = useState<Counters>({});
  return (
    <AttachmentsContext.Provider value={{ counters, setCounters }}>
      {children}
    </AttachmentsContext.Provider>
  );
};

export const useAttachmentsStatus = () => {
  const { counters } = useContext(AttachmentsContext);
  const numUploadingFiles = useMemo<number>(
    () => Object.keys(counters).reduce((acc, key) => acc + counters[key], 0),
    [counters]
  );
  return { numUploadingFiles };
};

export const useAttachmentsCounter = () => {
  const id = useMemo<string>(() => uniqueId(), []);
  const { setCounters } = useContext(AttachmentsContext);

  const increment = useCallback(() => {
    setCounters(state => ({ ...state, [id]: (state[id] ?? 0) + 1 }));
  }, [id, setCounters]);
  const decrement = useCallback(() => {
    setCounters(state => ({ ...state, [id]: (state[id] ?? 0) - 1 }));
  }, [id, setCounters]);

  useUnmount(() => {
    setCounters(state => {
      if (id in state) {
        const { [id]: amount, ...newState } = state;
        return newState;
      }
      return state;
    });
  });

  return { increment, decrement };
};
